import { CURRENCY_USD } from './constants';
import {
    collapseTripItems,
    compact,
    resolveItemListName,
    rollUpMatchType,
    sumItemsValue,
} from './items';
import { gtm } from '../gtm';

import type { EcommerceItem } from './types';

/**
 * The one way this app writes a GA4 ecommerce event.
 *
 * Every push is preceded by `{ ecommerce: null }`: GTM's dataLayer merges
 * rather than replaces, so without the reset an event inherits whatever items
 * the previous one left behind. Same pattern the legacy checkout's `purchase`
 * has used since it shipped (Checkout.jsx `sendBookingCompleteEvent`).
 */
function pushEcommerceEvent(event: string, ecommerce: Record<string, unknown>): void {
    gtm.pushToDataLayer({ ecommerce: null });
    // Compacted for the same reason the items are: a parameter the surface
    // could not supply is absent, not present-and-empty.
    gtm.pushToDataLayer({ ecommerce: compact(ecommerce), event });
}

/**
 * The currency the items' `price` fields are denominated in, for the two list
 * events. They carry no event-level `value` -- a list is not a basket, and
 * Google's own reference omits it -- but their items are priced, and the
 * contract puts all money in USD, so the denomination is stated rather than
 * left to be inferred. Omitted when nothing in the list has a price.
 */
function listCurrency(items: EcommerceItem[]): string | undefined {
    return items.some((item) => typeof item.price === 'number')
        ? CURRENCY_USD
        : undefined;
}

/**
 * Results rendered for one leg. Collapsed to one item per route × mode, so the
 * item count is the number of distinct routes on screen, not the number of
 * trips -- `quantity` carries that.
 */
function viewItemList({
    itemListId,
    items,
}: {
    itemListId?: string;
    items: EcommerceItem[];
}): void {
    if (!items.length) {
        return;
    }

    const collapsed = collapseTripItems(items);

    pushEcommerceEvent('view_item_list', {
        currency: listCurrency(collapsed),
        item_list_id: itemListId,
        item_list_name: resolveItemListName(itemListId),
        // Rolled up over the trips, not the collapsed items: a route with one
        // direct and one nearby trip is `mixed`, which the collapsed item alone
        // could not say.
        match_type: rollUpMatchType(items),
        items: collapsed,
    });
}

function selectItem({
    item,
    itemListId,
}: {
    item: EcommerceItem;
    itemListId?: string;
}): void {
    pushEcommerceEvent('select_item', {
        currency: listCurrency([item]),
        item_list_id: itemListId,
        item_list_name: resolveItemListName(itemListId),
        items: [item],
    });
}

function cartEvent(
    event: string,
    items: EcommerceItem[],
    extras: Record<string, unknown> = {},
): void {
    if (!items.length) {
        return;
    }

    const value = sumItemsValue(items);

    pushEcommerceEvent(event, {
        // Currency and value travel together or not at all. An event with a
        // currency and no amount reads as a free basket downstream; a trip
        // whose pricing has not resolved simply has no money on it yet.
        ...(value === undefined
            ? {}
            : { currency: CURRENCY_USD, value }),
        ...extras,
        items,
    });
}

/**
 * Fires only once a livecheck has resolved clean, at the live price. GA4
 * semantics put the items *added* in `value` here -- basket size is read off
 * the cart events instead.
 *
 * Carries the `match_type` rollup as well as the per-item value: this is the
 * intent signal the Ads audiences segment on, and event-scoped is the only
 * scope those audiences can read.
 */
function addToCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('add_to_cart', items, { match_type: rollUpMatchType(items) });
}

function removeFromCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('remove_from_cart', items);
}

/** The whole surviving basket, after the Trip Summary re-livecheck resolves. */
function viewCart({ items }: { items: EcommerceItem[] }): void {
    cartEvent('view_cart', items);
}

/**
 * `checkoutDestination` splits a partner deep-link click, which hands the
 * traveler to the carrier's own site, from a Wanderu checkout. Left off when the
 * caller has no way to tell rather than guessed at.
 */
function beginCheckout({
    checkoutDestination,
    items,
}: {
    checkoutDestination?: string;
    items: EcommerceItem[];
}): void {
    cartEvent('begin_checkout', items, checkoutDestination
        ? { checkout_destination: checkoutDestination }
        : {});
}

export {
    addToCart,
    beginCheckout,
    pushEcommerceEvent,
    removeFromCart,
    selectItem,
    viewCart,
    viewItemList,
};
